import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {useMemo, useState} from 'react';
import {ReactFlow, MarkerType} from 'reactflow';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import EntityMapControls from 'registry/entity-map/EntityMapControls';
import EntityMapEdge from 'registry/entity-map/EntityMapEdge';
import EntityMapSchemaFilter from 'registry/entity-map/EntityMapSchemaFilter';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';

import {DEFAULT_FIT_VIEW_SETTINGS} from './EntityMapConstants';
import {useEntityMapActorRef, useEntityMapSelector} from './EntityMapMachineProvider';
import EntityNode from './EntityNode';

const ENTITY_MAP_NODE_TYPES = {
  entity: EntityNode,
};

const ENTITY_MAP_EDGE_TYPES = {
  entityEdge: EntityMapEdge,
};

const useStyles = createStyles(() => ({
  container: {
    display: 'flex',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  graphArea: {
    flex: 1,
    height: '100%',
    position: 'relative',
  },
}));

/**
 * Renders a view of the entity map for a given entityId
 */
export default function EntityMapView(): JSX.Element {
  const {classes} = useStyles();
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const graphNodesById = useEntityMapSelector((s) => s.context.graphNodesById);
  const graphEdgesById = useEntityMapSelector((s) => s.context.graphEdgesById);
  const actorRef = useEntityMapActorRef();

  const [hiddenSchemas, setHiddenSchemas] = useState<Set<string>>(new Set());

  const visibleNodeIds = useMemo(() => {
    if (hiddenSchemas.size === 0) {
      return null;
    }
    const ids = new Set<string>();
    for (const node of graphNodesById.values()) {
      if (
        isInaccessible(node.data) ||
        node.data.schemaName == null ||
        !hiddenSchemas.has(node.data.schemaName)
      ) {
        ids.add(node.id);
      }
    }
    return ids;
  }, [graphNodesById, hiddenSchemas]);

  const graphNodes = useMemo(() => {
    const nodes = Array.from(graphNodesById.values());
    if (!visibleNodeIds) {
      return nodes;
    }
    return nodes.filter((node) => visibleNodeIds.has(node.id));
  }, [graphNodesById, visibleNodeIds]);

  const graphEdges = useMemo(() => {
    const edges = Array.from(graphEdgesById.values());
    if (!visibleNodeIds) {
      return edges;
    }
    return edges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));
  }, [graphEdgesById, visibleNodeIds]);

  return (
    <div className={classes.container}>
      <EntityMapSchemaFilter hiddenSchemas={hiddenSchemas} onChangeHiddenSchemas={setHiddenSchemas} />
      <div className={classes.graphArea}>
        <ReactFlow
          {...defaultReactFlowProps}
          defaultEdgeOptions={{
            style: {
              color: Color.GRAY_400,
            },
            markerEnd: {
              type: MarkerType.Arrow,
              width: 20,
              height: 20,
              color: Color.GRAY_400,
            },
            labelStyle: {
              color: Color.GRAY_800,
            },
          }}
          nodes={graphNodes}
          edges={graphEdges}
          nodeTypes={ENTITY_MAP_NODE_TYPES}
          edgeTypes={ENTITY_MAP_EDGE_TYPES}
          onlyRenderVisibleElements={true}
          fitView={true}
          fitViewOptions={DEFAULT_FIT_VIEW_SETTINGS}
          minZoom={0.1}
          deleteKeyCode={null}
          selectionKeyCode={null}
          nodesDraggable={false}
          onNodesChange={(changes) => {
            actorRef.send({type: 'onNodesChange', changes});
          }}
          onEdgesChange={(changes) => {
            actorRef.send({type: 'onEdgesChange', changes});
          }}
        >
          <DefaultBackground />
          <EntityMapControls />
        </ReactFlow>
      </div>
    </div>
  );
}
